import { Dialog, Transition } from '@headlessui/react';
import {
  AlertTriangleIcon,
  FileImageIcon,
  PlusIcon,
  ReceiptTextIcon,
} from 'lucide-react';

import { Fragment, useEffect, useRef, useState } from 'react';

import {
  type ItemAvailability,
  applyQuantity,
  availabilityOf,
  randomUuid,
  restLabelKind,
  restWarning,
  takeUnits,
  toggleUnit,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  LINE_ITEM_LIMITS,
  type LineItemSplitResult,
  type LineItemsRest,
  formatAmountForCurrency,
  fromMinorUnits,
  toMinorUnits,
} from '@splitfiesta/shared';
import { Avatar, Button } from '@splitfiesta/ui';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';

import { LineItemEditor, type LineItemFields } from './LineItemEditor';
import { LineItemRow } from './LineItemRow';
import { RestRow } from './RestRow';
import { ScanNotReconciledWarning } from './ScanNotReconciledWarning';
import {
  SplitByItemClaimContent,
  type SplitByItemClaimProps,
} from './SplitByItemClaimContent';
import { onRadioGroupKeyDown } from './radioGroupKeys';

export interface SplitByItemPerson {
  userId: string;
  name: string;
  profileImage?: string;
}

export interface SplitByItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The participants, in display order. */
  people: SplitByItemPerson[];
  currentUserId: string;
  lineItems: ExpenseLineItem[];
  rest: LineItemsRest;
  scanReconciled: boolean;
  /** A scan that did not add up, its list not edited since: the person must check it. */
  scanUnreconciled?: boolean;
  /** Null while the split cannot be worked out; the shares then stay empty. */
  preview: LineItemSplitResult | null;
  /** Entry amount and currency, the ones printed on the receipt. */
  amount: number;
  currency: string;
  /** A list edit: add, edit, delete. */
  onItemsChange: (items: ExpenseLineItem[]) => void;
  /** Who had what; kept apart because a pick is not a list edit. */
  onPicksChange: (items: ExpenseLineItem[]) => void;
  onRestChange: (rest: LineItemsRest) => void;
  /** Sets the form's amount (entry currency) to the items' sum; nothing is saved. */
  onSetTotal?: (amount: number) => void;
  /** Shown only when a receipt exists. */
  onViewReceipt?: () => void;
  /** The group cannot itemize (the example): the list is shown, not edited. */
  readOnly?: boolean;
}

type EditorTarget = { kind: 'add' } | { kind: 'edit'; id: string };

function SplitByItemContent({
  onClose,
  people,
  currentUserId,
  lineItems,
  rest,
  scanReconciled,
  scanUnreconciled = false,
  preview,
  amount,
  currency,
  onItemsChange,
  onPicksChange,
  onRestChange,
  onSetTotal,
  onViewReceipt,
  readOnly = false,
}: Omit<SplitByItemDialogProps, 'isOpen'>) {
  const { t } = useTranslation('expenses');
  const [selectedId, setSelectedId] = useState(currentUserId);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [confirmingTotal, setConfirmingTotal] = useState(false);
  // The confirmed action is gone, so the confirm has nothing to hand focus
  // back to; the new total is where it lands instead.
  const focusTotalRef = useRef(false);
  const totalRef = useRef<HTMLSpanElement>(null);
  // Stays until the next action or the dialog closes: a timer would expire
  // before a keyboard or screen-reader user can reach Undo.
  const [undo, setUndo] = useState<{
    item: ExpenseLineItem;
    index: number;
  } | null>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  // The editor hands focus back to the deleted row's pencil, which is gone.
  const focusUndoRef = useRef(false);
  useEffect(() => {
    if (undo && focusUndoRef.current) undoRef.current?.focus();
  }, [undo]);
  const onEditorGone = () => {
    if (focusUndoRef.current) undoRef.current?.focus();
    focusUndoRef.current = false;
  };

  const participantIds = people.map(p => p.userId);
  const selected =
    people.find(p => p.userId === selectedId) ??
    people.find(p => p.userId === currentUserId) ??
    people[0];
  const nameOf = (userId: string) =>
    people.find(p => p.userId === userId)?.name ?? '';
  const money = (value: number) => formatAmountForCurrency(value, currency);
  const shareOf = (userId: string) =>
    preview?.entryShares.find(s => s.userId === userId)?.amount;

  // Any new action ends the chance to undo the last delete.
  const change = (next: ExpenseLineItem[]) => {
    setUndo(null);
    onItemsChange(next);
  };

  // A click that moves nothing (a taken row, a clamped step) is not a pick.
  const pick = (next: ExpenseLineItem[]) => {
    setUndo(null);
    if (next !== lineItems) onPicksChange(next);
  };
  const toggle = (id: string) => {
    if (selected)
      pick(toggleUnit(lineItems, id, selected.userId, participantIds));
  };
  const setUnits = (id: string, units: number) => {
    if (selected)
      pick(takeUnits(lineItems, id, selected.userId, units, participantIds));
  };

  // Names under "Taken by" read in chip order, not in the order people picked.
  const rank = (userId: string) => {
    const index = participantIds.indexOf(userId);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  const availabilities = new Map<string, ItemAvailability>(
    lineItems.map(item => {
      const raw = availabilityOf(item, selected?.userId ?? '', participantIds);
      return [
        item.id,
        raw.kind === 'partial' || raw.kind === 'taken'
          ? {
              ...raw,
              takenBy: [...raw.takenBy].sort((a, b) => rank(a) - rank(b)),
            }
          : raw,
      ];
    })
  );
  // The shared-by-everyone rule is said once, on the first untouched row.
  const hintedId = lineItems.some(
    item => availabilities.get(item.id)?.kind !== 'free'
  )
    ? lineItems.find(item => availabilities.get(item.id)?.kind === 'free')?.id
    : undefined;

  const editing =
    editor?.kind === 'edit'
      ? (lineItems.find(item => item.id === editor.id) ?? null)
      : null;

  const save = (fields: LineItemFields) => {
    const unchanged =
      editing !== null &&
      editing.name.trim() === fields.name &&
      editing.amount === fields.amount &&
      editing.quantity === fields.quantity;
    if (unchanged) {
      setEditor(null);
      return;
    }
    if (editor?.kind === 'edit') {
      change(
        lineItems.map(item =>
          item.id === editor.id ? applyQuantity(item, fields) : item
        )
      );
    } else {
      change([
        ...lineItems,
        { id: randomUuid(), sharedBy: [], picks: [], ...fields },
      ]);
    }
    setEditor(null);
  };

  const remove = () => {
    if (!editing) return;
    const index = lineItems.findIndex(item => item.id === editing.id);
    onItemsChange(lineItems.filter(item => item.id !== editing.id));
    focusUndoRef.current = true;
    setUndo({ item: editing, index });
    setEditor(null);
  };

  const restore = () => {
    if (!undo) return;
    const next = [...lineItems];
    next.splice(Math.min(undo.index, next.length), 0, undo.item);
    change(next);
  };

  const itemsMinor = lineItems.reduce(
    (sum, item) => sum + toMinorUnits(item.amount, currency),
    0
  );
  const itemsTotal = fromMinorUnits(itemsMinor, currency);
  const restKind = preview
    ? restLabelKind(preview.restAmount, lineItems.length)
    : 'hidden';
  const warning =
    preview && lineItems.length > 0
      ? restWarning({
          restAmount: preview.restAmount,
          itemsTotal,
          amount,
          scanReconciled,
        })
      : 'none';
  const totalMinor = toMinorUnits(amount, currency);
  const setTotal =
    readOnly || !onSetTotal || itemsMinor <= 0 || itemsMinor === totalMinor
      ? null
      : itemsMinor > totalMinor
        ? 'above'
        : 'below';
  const setTotalAction = (
    <Button
      type="button"
      variant="ghost"
      onClick={() => {
        setUndo(null);
        setConfirmingTotal(true);
      }}
      data-testid="split-by-item-set-total"
      className="mt-2 min-h-11 text-left text-primary-600 dark:text-primary-400"
    >
      {t('lineItems.setTotalTo', { amount: money(itemsTotal) })}
    </Button>
  );
  const canAdd = !readOnly && lineItems.length < LINE_ITEM_LIMITS.MAX_ITEMS;
  const isSelf = selected?.userId === currentUserId;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Dialog.Title
          as="h2"
          className="min-w-0 flex-1 text-xl font-semibold text-gray-900 dark:text-gray-100"
        >
          {t('lineItems.splitByItemTitle')}
        </Dialog.Title>
        {onViewReceipt ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onViewReceipt}
            leftIcon={<FileImageIcon aria-hidden="true" className="h-4 w-4" />}
            className="min-h-11"
          >
            {t('scan.viewReceipt')}
          </Button>
        ) : null}
      </div>

      {scanUnreconciled ? <ScanNotReconciledWarning className="mt-3" /> : null}

      <div
        role="radiogroup"
        aria-labelledby={
          lineItems.length > 0 ? 'split-by-item-pick-line' : undefined
        }
        aria-label={lineItems.length > 0 ? undefined : t('lineItems.whoIsIn')}
        onKeyDown={onRadioGroupKeyDown}
        className="mt-4 flex flex-wrap gap-2"
      >
        {people.map(person => {
          const active = person.userId === selected?.userId;
          const share = shareOf(person.userId);
          return (
            <button
              key={person.userId}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => setSelectedId(person.userId)}
              className={`flex min-h-11 max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                active
                  ? 'border-primary-500 bg-primary-500 text-white dark:border-primary-400 dark:bg-primary-400 dark:text-gray-950'
                  : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'
              }`}
            >
              <span aria-hidden="true" className="shrink-0">
                <Avatar
                  name={person.name}
                  src={person.profileImage}
                  size="xs"
                />
              </span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span
                  className={`break-words text-xs ${
                    active
                      ? 'text-white dark:text-gray-950'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {person.name}
                </span>
                {share !== undefined ? (
                  <span className="text-sm font-semibold tabular-nums">
                    {money(share)}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {preview?.clamped.map(userId => (
        <p
          key={userId}
          className="mt-2 text-sm text-error-600 dark:text-error-400"
        >
          {t('lineItems.discountTooLarge', { name: nameOf(userId) })}
        </p>
      ))}

      {lineItems.length > 0 ? (
        <p
          id="split-by-item-pick-line"
          className="mt-3 text-sm text-gray-700 dark:text-gray-300"
        >
          {isSelf || !selected
            ? t('lineItems.pickWhatYouHad')
            : t('lineItems.pickWhatNameHad', { name: selected.name })}
        </p>
      ) : null}

      {lineItems.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center dark:border-gray-600">
          <ReceiptTextIcon
            aria-hidden="true"
            className="h-8 w-8 text-gray-400 dark:text-gray-500"
          />
          <p className="text-base font-medium text-gray-900 dark:text-gray-100">
            {t('lineItems.noItems')}
          </p>
          <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">
            {t('lineItems.noItemsHint')}
          </p>
          {canAdd ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => setEditor({ kind: 'add' })}
              leftIcon={<PlusIcon aria-hidden="true" className="h-4 w-4" />}
              className="mt-2 min-h-11"
            >
              {t('lineItems.addItem')}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="mt-3">
            {lineItems.map(item => (
              <LineItemRow
                key={item.id}
                item={item}
                availability={availabilities.get(item.id)!}
                nameOf={nameOf}
                currency={currency}
                selectedName={isSelf || !selected ? undefined : selected.name}
                unpickedHint={item.id === hintedId}
                readOnly={readOnly}
                onToggle={() => toggle(item.id)}
                onUnits={units => setUnits(item.id, units)}
                onEdit={() => setEditor({ kind: 'edit', id: item.id })}
              />
            ))}
          </ul>

          {restKind !== 'hidden' && preview ? (
            <RestRow
              label={t(
                restKind === 'restNegative'
                  ? 'lineItems.restNegative'
                  : 'lineItems.rest'
              )}
              amount={money(preview.restAmount)}
              mode={rest.mode}
              onChange={
                readOnly
                  ? undefined
                  : next => {
                      setUndo(null);
                      onRestChange(next);
                    }
              }
            />
          ) : null}

          {setTotal === 'below' ? setTotalAction : null}

          {warning !== 'none' && preview ? (
            <p className="mt-2 flex items-start gap-2 text-sm text-warning-700 dark:text-warning-400">
              <AlertTriangleIcon
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span>
                {warning === 'missingItems'
                  ? t('lineItems.restWarning', {
                      amount: money(preview.restAmount),
                    })
                  : t('lineItems.restOverWarning')}
              </span>
            </p>
          ) : null}

          {setTotal === 'above' ? setTotalAction : null}

          {canAdd ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditor({ kind: 'add' })}
              leftIcon={<PlusIcon aria-hidden="true" className="h-4 w-4" />}
              className="mt-3 min-h-11"
            >
              {t('lineItems.addItem')}
            </Button>
          ) : null}
        </>
      )}

      <div
        data-testid="split-by-item-footer"
        className="sticky bottom-0 -mx-4 -mb-5 mt-4 border-t border-gray-200 bg-white px-4 pb-5 pt-3 sm:-mx-6 sm:px-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div aria-live="polite">
          {undo ? (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg bg-gray-900 px-4 py-2 text-white dark:bg-gray-100 dark:text-gray-900">
              <span className="flex-1 text-sm">
                {t('lineItems.itemDeleted')}
              </span>
              <button
                ref={undoRef}
                type="button"
                onClick={restore}
                className="min-h-11 rounded-md px-3 text-sm font-semibold text-primary-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-700"
              >
                {t('lineItems.undo')}
              </button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            ref={totalRef}
            tabIndex={-1}
            data-testid="split-by-item-total"
            className="min-w-[45%] flex-1 rounded text-base font-semibold text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-100"
          >
            {t('lineItems.total', { amount: money(amount) })}
          </span>
          <Button
            type="button"
            variant="primary"
            onClick={onClose}
            className="ml-auto min-h-11"
          >
            {t('conversion.done')}
          </Button>
        </div>
      </div>

      <LineItemEditor
        isOpen={editor !== null}
        item={editing}
        currency={currency}
        onSave={save}
        onDelete={editing ? remove : undefined}
        onClose={() => setEditor(null)}
        onAfterLeave={onEditorGone}
      />
      <ConfirmationDialog
        isOpen={confirmingTotal}
        onClose={() => setConfirmingTotal(false)}
        onConfirm={() => {
          focusTotalRef.current = true;
          setConfirmingTotal(false);
          onSetTotal?.(itemsTotal);
        }}
        onAfterLeave={() => {
          if (focusTotalRef.current) totalRef.current?.focus();
          focusTotalRef.current = false;
        }}
        title={t('lineItems.setTotalConfirmTitle', {
          amount: money(itemsTotal),
        })}
        description={t('lineItems.setTotalConfirmBody')}
        confirmText={t('lineItems.setTotalConfirm')}
        cancelText={t('common:actions.cancel')}
      />
    </>
  );
}

export interface SplitByItemClaimDialogProps {
  isOpen: boolean;
  /** Called once closing is settled: unsaved picks are confirmed first. */
  onClose: () => void;
  claim: SplitByItemClaimProps;
}

export function SplitByItemDialog(
  props: SplitByItemDialogProps | SplitByItemClaimDialogProps
) {
  const { t } = useTranslation();
  const [askingDiscard, setAskingDiscard] = useState(false);
  const claim = 'claim' in props ? props.claim : null;
  const requestClose = () => {
    if (claim?.saving) return;
    if (claim?.dirty) setAskingDiscard(true);
    else props.onClose();
  };

  return (
    <Transition show={props.isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={requestClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 dark:bg-black/60" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200 motion-reduce:transition-none"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150 motion-reduce:transition-none"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-2xl bg-white px-4 py-5 shadow-xl sm:rounded-2xl sm:px-6 dark:bg-gray-800">
                {'claim' in props ? (
                  <>
                    <SplitByItemClaimContent
                      {...props.claim}
                      onCancel={requestClose}
                    />
                    {/* Inside the panel, so headless treats it as a nested dialog. */}
                    <ConfirmationDialog
                      isOpen={askingDiscard}
                      onClose={() => setAskingDiscard(false)}
                      onConfirm={() => {
                        setAskingDiscard(false);
                        props.onClose();
                      }}
                      title={t('mobile:confirmDiscard.title')}
                      description={t('mobile:confirmDiscard.body')}
                      confirmText={t('mobile:confirmDiscard.discard')}
                      cancelText={t('mobile:confirmDiscard.keepEditing')}
                      variant="danger"
                    />
                  </>
                ) : (
                  <SplitByItemContent {...withoutOpen(props)} />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

const withoutOpen = ({
  isOpen: _isOpen,
  ...rest
}: SplitByItemDialogProps): Omit<SplitByItemDialogProps, 'isOpen'> => rest;

export default SplitByItemDialog;
